import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SHEET_LINKS, SheetLinkKey } from '@/components/catalog/ProductSheetFields';

// Procedimiento «Catálogo de producto» (VIDA-372): qué enseña la ficha de cada
// enlace y qué espacios adicionales tiene la portada. Todo es opcional: sin
// configurar, la web se ve como hasta ahora.

export const SHEET_FIELDS = [
  { key: 'modality', label: 'Modalidad' },
  { key: 'date', label: 'Fecha' },
  { key: 'duration', label: 'Duración' },
  { key: 'seals', label: 'Sellos' },
  { key: 'accreditation', label: 'Acreditación' },
  { key: 'interest', label: 'Aspectos de interés' },
] as const;
type SheetFieldKey = (typeof SHEET_FIELDS)[number]['key'];

const BOTTOM_ZONES = [
  { key: 'categories', label: 'Categorías comerciales del enlace' },
  { key: 'children', label: 'Enlaces que contiene' },
  { key: 'related', label: '«Otros que te pueden interesar» (de su misma categoría)' },
] as const;
type BottomZone = (typeof BOTTOM_ZONES)[number]['key'];

export interface ProductDetailStructure {
  show_tags?: boolean;
  fields?: Partial<Record<SheetFieldKey, boolean>>;
  links?: Partial<Record<SheetLinkKey, boolean>>;
  bottom?: Partial<Record<BottomZone, boolean>>;
}

export interface LinkedItem {
  title: string;
  description?: string;
  image?: string;
  link?: string;
}

export interface CatalogSpaces {
  show_categories?: boolean;
  show_cta?: boolean;
  cta_link?: string;
  cta_central?: { enabled: boolean; heading: string; text: string; image: string; comments: string[]; link?: string };
  subsidy?: { enabled: boolean; text: string; logos: { image: string; alt: string; link?: string }[]; link?: string };
  related_spaces?: { enabled: boolean; heading: string; items: LinkedItem[] };
}

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const smallLabel = 'block text-xs text-gray-500 mb-0.5';

type ImageField = (props: { value: string; onChange: (url: string) => void }) => JSX.Element;

function Check({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded border-gray-300 text-vida-600 focus:ring-vida-500" />
      {label}
    </label>
  );
}

/** Casillas de la estructura de la ficha. Sin marcar nada = todo visible (si tiene datos). */
export function ProductDetailStructureEditor({ value, onChange }: { value: ProductDetailStructure | undefined; onChange: (v: ProductDetailStructure) => void }) {
  const v = value ?? {};
  const on = (group: Record<string, boolean | undefined> | undefined, key: string) => group?.[key] !== false;
  const toggle = (group: 'fields' | 'links' | 'bottom', key: string, checked: boolean) =>
    onChange({ ...v, [group]: { ...(v[group] ?? {}), [key]: checked } });

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Zona central</h4>
        <Check checked={v.show_tags !== false} onChange={(c) => onChange({ ...v, show_tags: c })} label="Etiquetado del enlace" />
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Zona derecha: datos</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {SHEET_FIELDS.map((f) => <Check key={f.key} checked={on(v.fields, f.key)} onChange={(c) => toggle('fields', f.key, c)} label={f.label} />)}
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Zona derecha: enlaces</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {SHEET_LINKS.map((l) => <Check key={l.key} checked={on(v.links, l.key)} onChange={(c) => toggle('links', l.key, c)} label={l.label} />)}
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Zona inferior</h4>
        <div className="space-y-2">
          {BOTTOM_ZONES.map((z) => <Check key={z.key} checked={on(v.bottom, z.key)} onChange={(c) => toggle('bottom', z.key, c)} label={z.label} />)}
        </div>
      </div>
      <p className="text-xs text-gray-500">Un dato marcado solo aparece si el enlace lo tiene relleno (pestaña «Ficha» del enlace).</p>
    </div>
  );
}

function LinkedItemsEditor({ items, onChange, ImageField }: { items: LinkedItem[]; onChange: (items: LinkedItem[]) => void; ImageField: ImageField }) {
  const set = (idx: number, field: keyof LinkedItem, value: string) => onChange(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <input type="text" value={item.title} onChange={(e) => set(idx, 'title', e.target.value)} className={inputClass} placeholder="Título" />
            <button onClick={() => onChange(items.filter((_, i) => i !== idx))} className="p-1.5 text-red-400 hover:text-red-600" aria-label="Quitar">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
          <input type="text" value={item.description ?? ''} onChange={(e) => set(idx, 'description', e.target.value)} className={inputClass} placeholder="Descripción" />
          <input type="text" value={item.link ?? ''} onChange={(e) => set(idx, 'link', e.target.value.trim())} className={inputClass} placeholder="https://otro-catalogo… o /ruta" />
          <ImageField value={item.image ?? ''} onChange={(url) => set(idx, 'image', url)} />
        </div>
      ))}
      <button onClick={() => onChange([...items, { title: '' }])} className="inline-flex items-center gap-1 text-sm text-vida-600 hover:text-vida-700">
        <PlusIcon className="w-4 h-4" /> Añadir
      </button>
    </div>
  );
}

/** Espacios de la portada que se activan o desactivan. */
export function SpacesEditor({ value, onChange, ImageField }: { value: CatalogSpaces | undefined; onChange: (v: CatalogSpaces) => void; ImageField: ImageField }) {
  const v = value ?? {};
  const central = v.cta_central ?? { enabled: false, heading: '', text: '', image: '', comments: ['', '', '', ''] };
  const subsidy = v.subsidy ?? { enabled: false, text: '', logos: [] };
  const related = v.related_spaces ?? { enabled: false, heading: 'Otros que te pueden interesar', items: [] };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Check checked={v.show_categories !== false} onChange={(c) => onChange({ ...v, show_categories: c })} label="Espacio de categorías de interés (grid de categorías)" />
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-4">
        <Check checked={central.enabled} onChange={(c) => onChange({ ...v, cta_central: { ...central, enabled: c } })} label="Llamada a la acción central" />
        {central.enabled && (
          <div className="space-y-3 pl-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={smallLabel}>Título</label>
                <input type="text" value={central.heading} onChange={(e) => onChange({ ...v, cta_central: { ...central, heading: e.target.value } })} className={inputClass} />
              </div>
              <div>
                <label className={smallLabel}>Enlace (opcional)</label>
                <input type="text" value={central.link ?? ''} onChange={(e) => onChange({ ...v, cta_central: { ...central, link: e.target.value.trim() } })} className={inputClass} placeholder="https://… o /ruta" />
              </div>
            </div>
            <div>
              <label className={smallLabel}>Texto</label>
              <textarea value={central.text} onChange={(e) => onChange({ ...v, cta_central: { ...central, text: e.target.value } })} rows={2} className={inputClass} />
            </div>
            <div>
              <label className={smallLabel}>Imagen</label>
              <ImageField value={central.image} onChange={(url) => onChange({ ...v, cta_central: { ...central, image: url } })} />
            </div>
            <div>
              <label className={smallLabel}>Cuatro comentarios que refuerzan el mensaje</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <input key={i} type="text" value={central.comments[i] ?? ''} className={inputClass} placeholder={`Comentario ${i + 1}`}
                    onChange={(e) => {
                      const comments = [0, 1, 2, 3].map((j) => (j === i ? e.target.value : central.comments[j] ?? ''));
                      onChange({ ...v, cta_central: { ...central, comments } });
                    }} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-4">
        <Check checked={v.show_cta !== false} onChange={(c) => onChange({ ...v, show_cta: c })} label="Llamada a la acción inferior (título, texto e imagen de «Títulos de secciones» e «Imagen CTA»)" />
        {v.show_cta !== false && (
          <div className="pl-6">
            <label className={smallLabel}>A dónde lleva su botón (vacío = al catálogo)</label>
            <input type="text" value={v.cta_link ?? ''} onChange={(e) => onChange({ ...v, cta_link: e.target.value.trim() })} className={inputClass} placeholder="/catalogo" />
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-4">
        <Check checked={subsidy.enabled} onChange={(c) => onChange({ ...v, subsidy: { ...subsidy, enabled: c } })} label="Banner de subvenciones (encima del pie de página)" />
        {subsidy.enabled && (
          <div className="space-y-3 pl-6">
            <div>
              <label className={smallLabel}>Texto que exige la subvención</label>
              <textarea value={subsidy.text} onChange={(e) => onChange({ ...v, subsidy: { ...subsidy, text: e.target.value } })} rows={2} className={inputClass} />
            </div>
            <div>
              <label className={smallLabel}>Enlace (opcional)</label>
              <input type="text" value={subsidy.link ?? ''} onChange={(e) => onChange({ ...v, subsidy: { ...subsidy, link: e.target.value.trim() } })} className={inputClass} placeholder="https://…" />
            </div>
            <div className="space-y-2">
              <label className={smallLabel}>Logotipos</label>
              {subsidy.logos.map((logo, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1">
                    <ImageField value={logo.image} onChange={(url) => onChange({ ...v, subsidy: { ...subsidy, logos: subsidy.logos.map((l, j) => (j === i ? { ...l, image: url } : l)) } })} />
                  </div>
                  <input type="text" value={logo.alt} placeholder="Nombre de la entidad" className={`max-w-[200px] ${inputClass}`}
                    onChange={(e) => onChange({ ...v, subsidy: { ...subsidy, logos: subsidy.logos.map((l, j) => (j === i ? { ...l, alt: e.target.value } : l)) } })} />
                  <button onClick={() => onChange({ ...v, subsidy: { ...subsidy, logos: subsidy.logos.filter((_, j) => j !== i) } })} className="p-1.5 text-red-400 hover:text-red-600" aria-label="Quitar logotipo">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button onClick={() => onChange({ ...v, subsidy: { ...subsidy, logos: [...subsidy.logos, { image: '', alt: '' }] } })} className="inline-flex items-center gap-1 text-sm text-vida-600 hover:text-vida-700">
                <PlusIcon className="w-4 h-4" /> Añadir logotipo
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-4">
        <Check checked={related.enabled} onChange={(c) => onChange({ ...v, related_spaces: { ...related, enabled: c } })} label="Otros que te pueden interesar (otros catálogos, ecosistemas o soluciones)" />
        {related.enabled && (
          <div className="space-y-3 pl-6">
            <div>
              <label className={labelClass}>Título</label>
              <input type="text" value={related.heading} onChange={(e) => onChange({ ...v, related_spaces: { ...related, heading: e.target.value } })} className={inputClass} />
            </div>
            <LinkedItemsEditor items={related.items} onChange={(items) => onChange({ ...v, related_spaces: { ...related, items } })} ImageField={ImageField} />
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500">El espacio de publicidad llegará con el procedimiento «Planes VIDA».</p>
    </div>
  );
}
